import moment from 'moment';
import { t } from '../../utils/i18n';
import { handleEventRoomNullUI, handleEventRoomUI } from "../../modules/actions/appointmentConfirmedActions";
import { getProviderFullName } from "../../modules/actions/providerActions";
import { getClassScreen, navigate, params } from "../../modules/events/movePages";
import { SCREEN } from "../../utils/constants";
import { loadAppointmentGonePage } from "./appointmentConfirmed";
import { eventPayload, eventRoom, form, primaryColor } from "../../utils/variables";
import { toastError } from "./error";
import { showCardRequiredNotice } from "./common/cardRequiredNotice";
import { cancelEvent, primeRescheduleSignatureParams } from "../../services/selectTimeService";
import {
    SELECTORS
} from "../../utils/appointmentCancelConstants";
import { toastSuccess } from './common/toast';

export async function loadAppointmentCancel() {
    if (!eventPayload) return;

    primeRescheduleSignatureParams();
    initializeStyles();
    setBusinessLogo();
    renderAppointmentInfo();
    handleCalendarAndDirections();
    handleConfirmCancelButtons();
}

function initializeStyles() {
    const container = document.querySelector(SELECTORS.container);
    if (!container) return;

    document.querySelector(SELECTORS.icon).style.backgroundColor = `${primaryColor}0f`;
    document.querySelector(SELECTORS.informationCard).style.backgroundColor = `${primaryColor}09`;

    const checkIconWrapper = document.querySelector(SELECTORS.checkIconWrapper);
    checkIconWrapper.style.border = `3px solid ${primaryColor}0f`;
    checkIconWrapper.style.backgroundColor = 'white';
}

function setBusinessLogo() {
    if (form.project.business_photo) {
        document.querySelector(SELECTORS.logo)?.setAttribute('src', form.project.business_photo);
    }
}

function renderAppointmentInfo() {
    const startTime = parseInt(eventPayload.start_time);
    const staffName = getStaffName();

    const staffNameDom = document.querySelector(SELECTORS.staffInfo);
    const timeInfoDom = document.querySelector(SELECTORS.timeInfo);
    const dateInfoDom = document.querySelector(SELECTORS.dateInfo);

    if (staffNameDom) staffNameDom.textContent = staffName;
    if (timeInfoDom) timeInfoDom.textContent = moment(startTime * 1000).format('hh:mm A');

    if (dateInfoDom) {
        const formatDuration = (durationMinutes) => {
            const duration = moment.duration(durationMinutes, 'minutes');
            const hours = Math.floor(duration.asHours());
            const minutes = duration.minutes();

            let result = '';
            if (hours > 0) {
                result += `${hours}h `;
            }
            if (minutes > 0 || hours === 0) {
                result += `${minutes}min`;
            }
            return result;
        };

        const appointmentDate = moment(startTime * 1000).format('dddd, MMMM DD, YYYY');
        const durationFormatted = formatDuration(eventPayload.duration);

        dateInfoDom.innerHTML = `
            ${appointmentDate} - <span style="background: ${primaryColor}0f">${durationFormatted}</span>
        `;
    }
}

function getStaffName() {
    if (eventPayload.participants && eventPayload.participants.length > 0 && params('event_id')) {
        const participant = eventPayload.participants.find(i => i.participable_type === 'App\\Models\\Staff');
        return participant ? `${participant.participable.first_name} ${participant.participable.last_name}` : '';
    }
    return getProviderFullName(eventPayload.staff);
}

function handleCalendarAndDirections() {
    if (eventRoom === null) {
        handleEventRoomNullUI();
    } else {
        handleEventRoomUI();
    }
}

function handleConfirmCancelButtons() {
    const actionDiv = document.querySelector(SELECTORS.actionDiv);
    if (!actionDiv) return;

    showCancellationForm();
}

function showCancellationForm() {
    const actionDiv = document.querySelector(SELECTORS.actionDiv);
    if (!actionDiv) return;

    const formHtml = `
        <div class="cancellation-form">
            <div class="cancellation-form__header">
                <h3>${t('why_cancelling')} <span class="required">*</span></h3>
            </div>
            <div class="cancellation-form__reasons">
                <button type="button" class="reason-btn" data-reason="Emergency">${t('cancel_reason_emergency')}</button>
                <button type="button" class="reason-btn" data-reason="Conflict">${t('cancel_reason_conflict')}</button>
                <button type="button" class="reason-btn" data-reason="Not needed">${t('cancel_reason_not_needed')}</button>
            </div>
            <div class="cancellation-form__textarea">
                <textarea
                    id="cancellation-reason"
                    placeholder="${t('cancel_reason_placeholder')}"
                    maxlength="300"
                    rows="4"
                    required
                ></textarea>
                <div class="character-count">
                    <span id="char-count">0</span>/300
                </div>
            </div>
            <div class="cancellation-form__actions">
                <button type="button" id="cancel-appointment-btn" class="cancel-appointment-btn" disabled>
                    ${t('cancel_appointment')}
                </button>
            </div>
        </div>
    `;

    actionDiv.innerHTML = formHtml;

    if (typeof feather !== 'undefined') {
        feather.replace();
    }

    setupCancellationFormListeners();
}

function setupCancellationFormListeners() {
    const textarea = document.getElementById('cancellation-reason');
    const charCount = document.getElementById('char-count');
    const cancelBtn = document.getElementById('cancel-appointment-btn');
    const reasonBtns = document.querySelectorAll('.reason-btn');

    if (textarea && charCount) {
        textarea.addEventListener('input', function () {
            const length = this.value.length;
            charCount.textContent = length;

            if (cancelBtn) {
                cancelBtn.disabled = length < 2;
            }
        });
    }

    reasonBtns.forEach(btn => {
        btn.addEventListener('click', function () {
            reasonBtns.forEach(b => b.classList.remove('active'));

            this.classList.add('active');

            if (textarea) {
                const reason = this.getAttribute('data-reason');
                textarea.value = reason;
                charCount.textContent = reason.length;
                if (cancelBtn) cancelBtn.disabled = false;
            }
        });
    });

    if (cancelBtn) {
        cancelBtn.addEventListener('click', handleCancelAppointment);
    }
}

async function handleCancelAppointment() {
    const textarea = document.getElementById('cancellation-reason');
    const cancelBtn = document.getElementById('cancel-appointment-btn');

    if (!textarea || !textarea.value.trim()) {
        toastError(t('cancel_provide_reason'));
        return;
    }

    const isPredefinedReason = document.querySelector('.reason-btn.active') !== null;
    if (!isPredefinedReason && textarea.value.trim().length < 10) {
        toastError(t('cancel_min_chars'));
        return;
    }

    try {
        if (cancelBtn) {
            cancelBtn.disabled = true;
            cancelBtn.innerHTML = t('processing');
        }

        const eventId = params('event_id');
        if (!eventId) {
            throw new Error('Event ID not found');
        }

        const response = await cancelEvent(eventId, textarea.value.trim());

        if (response && response.status === 200) {
            toastSuccess(t('cancel_success'));
            // Stay in the widget: strip the action/signature params so a
            // refresh lands on the plain revisit URL, then render the
            // cancelled page in place. No page-level redirect — it reloaded
            // the host page out from under the visitor.
            try {
                const url = new URL(window.location.href);
                ['action', 'signature', 'expires', 'resume_signature', 'resume_expires'].forEach((p) => url.searchParams.delete(p));
                window.history.replaceState({}, '', url.toString());
            } catch (_) { /* URL cleanup is cosmetic */ }
            loadAppointmentGonePage('cancelled');
            const screenEl = document.querySelector(getClassScreen(SCREEN.APPOINTMENT_CONFIRMED));
            if (screenEl) screenEl.style.transition = 'none';
            navigate(SCREEN.APPOINTMENT_CONFIRMED);
        } else {
            throw new Error('Failed to cancel appointment');
        }
    } catch (error) {
        if (!showCardRequiredNotice(error, cancelBtn)) {
            toastError(t('cancel_failed'));
        }

        if (cancelBtn) {
            cancelBtn.disabled = false;
            cancelBtn.innerHTML = t('cancel_appointment');
        }
    }
}
